import Fonts from './shared/Fonts';
import Countdown from './shared/Countdown';
import {
  AngelWings,
  GoldDivider,
  OrnateCorner,
  CrossIcon,
  Dove,
  RosetteDivider,
} from './shared/Decorations';

import Gallery from './shared/Gallery';

export default function BautizoDorado({ data, title, media = [] }) {
  const d = data || {};
  const child = d.child || 'Isabella';
  const date = d.date ? new Date(d.date) : null;

  const dateStr = date
    ? date.toLocaleDateString('es-PE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const dateShort = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  // Paleta elegante
  const gold = '#c8a961';
  const goldSoft = '#e0c98a';
  const cream = '#fdf8ed';
  const creamDark = '#f5ecd9';
  const dark = '#3a3a3a';
  const muted = '#6b6b6b';

  // Tipografías
  const elegant = "'Cinzel', serif";
  const script = "'Cormorant Garamond', serif";

  // Versículo por defecto
  const verse =
    d.verse ||
    'Dejad a los niños venir a mí, y no se lo impidáis; porque de los tales es el reino de los cielos.';
  const verseRef = d.verseRef || 'Mateo 19:14';

  // Padrinos (opcional, desde data)
  const godparents = Array.isArray(d.godparents) ? d.godparents : [];

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${cream} 0%, ${creamDark} 50%, ${cream} 100%)`,
      }}
    >
      <Fonts for="bautizo" />

      {/* ---------- MARCO DOBLE DECORATIVO ---------- */}
      <div
        className="absolute inset-3 sm:inset-5 border pointer-events-none z-10"
        style={{ borderColor: gold, opacity: 0.35 }}
      />
      <div
        className="absolute inset-5 sm:inset-8 border pointer-events-none z-10"
        style={{ borderColor: gold, opacity: 0.18 }}
      />

      {/* Esquinas ornamentadas */}
      <OrnateCorner
        color={gold}
        className="absolute top-3 left-3 w-20 h-20 sm:w-28 sm:h-28 z-10 opacity-70"
      />
      <OrnateCorner
        color={gold}
        className="absolute top-3 right-3 w-20 h-20 sm:w-28 sm:h-28 z-10 opacity-70 -scale-x-100"
      />
      <OrnateCorner
        color={gold}
        className="absolute bottom-3 left-3 w-20 h-20 sm:w-28 sm:h-28 z-10 opacity-70 -scale-y-100"
      />
      <OrnateCorner
        color={gold}
        className="absolute bottom-3 right-3 w-20 h-20 sm:w-28 sm:h-28 z-10 opacity-70 -scale-100"
      />

      {/* ---------- HERO ---------- */}
      <section className="relative px-8 pt-28 pb-16 sm:pt-36 sm:pb-24">
        <div className="max-w-3xl mx-auto text-center relative">
          <AngelWings color={gold} className="w-40 sm:w-56 h-16 sm:h-20 mx-auto mb-6" />

          <p
            className="text-[10px] sm:text-xs tracking-[0.6em] uppercase mb-6"
            style={{ color: gold, fontFamily: elegant, fontWeight: 600 }}
          >
            Sacramento del Bautismo
          </p>

          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-12" style={{ background: gold, opacity: 0.6 }} />
            <Dove color={gold} className="w-10 h-8" />
            <div className="h-px w-12" style={{ background: gold, opacity: 0.6 }} />
          </div>

          <p
            className="text-sm italic mb-4"
            style={{ fontFamily: script, color: muted }}
          >
            Con la bendición de Dios y el amor de su familia
          </p>

          <h1
            className="text-5xl sm:text-7xl md:text-8xl leading-tight mb-6"
            style={{ fontFamily: elegant, color: dark, fontWeight: 600, letterSpacing: '0.02em' }}
          >
            {child}
          </h1>

          <div className="flex justify-center my-8">
            <CrossIcon color={gold} className="w-6 h-9 opacity-70" />
          </div>

          <p
            className="text-lg sm:text-xl leading-relaxed max-w-xl mx-auto"
            style={{ fontFamily: script, color: muted, fontStyle: 'italic' }}
          >
            {title || 'Te invitamos a acompañarnos en este día tan especial'}
          </p>

          {dateShort && (
            <div className="mt-10 inline-flex flex-col items-center">
              <RosetteDivider color={gold} className="w-56 h-8 mb-4 opacity-80" />
              <p
                className="text-2xl sm:text-3xl tracking-[0.15em] uppercase"
                style={{ fontFamily: elegant, color: gold, fontWeight: 500 }}
              >
                {dateShort}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ---------- VERSÍCULO BÍBLICO ---------- */}
      <section className="relative px-6 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto">
          <div
            className="relative bg-white/60 backdrop-blur-sm rounded-2xl px-8 sm:px-16 py-12 text-center"
            style={{
              border: `1px solid ${gold}30`,
              boxShadow: `0 20px 60px -20px ${gold}20`,
            }}
          >
            <div className="absolute -top-5 left-1/2 -translate-x-1/2">
              <div
                className="w-10 h-10 rounded-full grid place-items-center"
                style={{ background: cream, border: `1px solid ${gold}40` }}
              >
                <CrossIcon color={gold} className="w-3 h-5" />
              </div>
            </div>

            <div
              className="text-6xl leading-none mb-2"
              style={{ color: gold, fontFamily: elegant, opacity: 0.35 }}
            >
              "
            </div>

            <p
              className="text-lg sm:text-2xl leading-relaxed italic"
              style={{ fontFamily: script, color: dark }}
            >
              {verse}
            </p>

            <GoldDivider color={gold} className="w-40 h-6 mx-auto my-6 opacity-70" />

            <p
              className="text-xs sm:text-sm tracking-[0.3em] uppercase"
              style={{ color: gold, fontFamily: elegant, fontWeight: 500 }}
            >
              {verseRef}
            </p>
          </div>
        </div>
      </section>

      {/* ---------- CUENTA REGRESIVA ---------- */}
      {date && (
        <section className="px-6 py-16">
          <div className="max-w-3xl mx-auto text-center">
            <p
              className="text-[10px] sm:text-xs tracking-[0.5em] uppercase mb-3"
              style={{ color: gold, fontFamily: elegant }}
            >
              Faltan
            </p>
            <p
              className="text-2xl sm:text-3xl mb-10"
              style={{ fontFamily: script, color: dark, fontStyle: 'italic' }}
            >
              para este gran día
            </p>
            <Countdown target={date} accent={gold} labelColor="#9a8863" />
          </div>
        </section>
      )}

      {/* ---------- CEREMONIA Y RECEPCIÓN ---------- */}
      <section className="px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <RosetteDivider color={gold} className="w-64 h-8 mx-auto mb-12 opacity-80" />

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Ceremonia */}
            <div className="text-center md:text-right md:pr-8">
              <div className="inline-flex items-center gap-3 mb-4 md:flex-row-reverse">
                <div
                  className="w-12 h-12 rounded-full grid place-items-center flex-shrink-0"
                  style={{ background: creamDark, border: `1px solid ${gold}40` }}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke={gold} strokeWidth="1.2">
                    <path d="M12 2 L12 12 M5 8 L19 8 M8 22 L12 15 L16 22" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="1.5" fill={gold} />
                  </svg>
                </div>
                <h3
                  className="text-[10px] tracking-[0.5em] uppercase"
                  style={{ color: gold, fontFamily: elegant, fontWeight: 600 }}
                >
                  Ceremonia
                </h3>
              </div>

              <p
                className="text-2xl sm:text-3xl capitalize mb-2"
                style={{ fontFamily: script, color: dark, fontWeight: 600 }}
              >
                {dateStr}
              </p>

              {d.time && (
                <p
                  className="text-lg mb-6"
                  style={{ fontFamily: script, color: muted }}
                >
                  {d.time}
                </p>
              )}

              {d.church && (
                <>
                  <p
                    className="text-base mb-1"
                    style={{ fontFamily: script, color: dark, fontWeight: 600 }}
                  >
                    {d.church}
                  </p>
                </>
              )}

              {d.churchAddress && (
                <p
                  className="text-sm"
                  style={{ fontFamily: script, color: muted }}
                >
                  {d.churchAddress}
                </p>
              )}
            </div>

            {/* Separador vertical */}
            <div className="hidden md:block absolute left-1/2 top-32 bottom-32 w-px -translate-x-1/2"
              style={{ background: `linear-gradient(180deg, transparent, ${gold}40, transparent)` }}
            />

            {/* Recepción */}
            {(d.place || d.address) && (
              <div className="text-center md:text-left md:pl-8">
                <div className="inline-flex items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-full grid place-items-center flex-shrink-0"
                    style={{ background: creamDark, border: `1px solid ${gold}40` }}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke={gold} strokeWidth="1.2">
                      <path d="M8 22 L8 12 M16 22 L16 12 M5 12 L19 12 L18 8 L6 8 Z M10 4 L14 4 M12 4 L12 8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3
                    className="text-[10px] tracking-[0.5em] uppercase"
                    style={{ color: gold, fontFamily: elegant, fontWeight: 600 }}
                  >
                    Celebración
                  </h3>
                </div>

                {d.place && (
                  <p
                    className="text-2xl sm:text-3xl mb-2"
                    style={{ fontFamily: script, color: dark, fontWeight: 600 }}
                  >
                    {d.place}
                  </p>
                )}

                {d.time && (
                  <p
                    className="text-lg mb-6"
                    style={{ fontFamily: script, color: muted }}
                  >
                    Después de la ceremonia
                  </p>
                )}

                {d.address && (
                  <p
                    className="text-sm"
                    style={{ fontFamily: script, color: muted }}
                  >
                    {d.address}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ---------- PADRINOS ---------- */}
      {godparents.length > 0 && (
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <GoldDivider color={gold} className="w-40 h-6 mx-auto mb-6 opacity-70" />

            <p
              className="text-[10px] tracking-[0.5em] uppercase mb-3"
              style={{ color: gold, fontFamily: elegant, fontWeight: 600 }}
            >
              Con la bendición de
            </p>
            <p
              className="text-3xl sm:text-4xl mb-12"
              style={{ fontFamily: script, color: dark, fontStyle: 'italic' }}
            >
              Nuestros Padrinos
            </p>

            <div className="grid sm:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {godparents.map((gp, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl px-8 py-6"
                  style={{
                    border: `1px solid ${gold}30`,
                    boxShadow: `0 10px 30px -10px ${gold}25`,
                  }}
                >
                  <p
                    className="text-[10px] tracking-[0.4em] uppercase mb-3"
                    style={{ color: gold, fontFamily: elegant }}
                  >
                    {gp.role || (i === 0 ? 'Padrino' : 'Madrina')}
                  </p>
                  <p
                    className="text-xl sm:text-2xl"
                    style={{ fontFamily: script, color: dark, fontWeight: 600 }}
                  >
                    {gp.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- MENSAJE ---------- */}
      {d.description && (
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <CrossIcon color={gold} className="w-5 h-8 mx-auto mb-6 opacity-60" />

            <div
              className="relative bg-white rounded-3xl px-8 sm:px-14 py-12"
              style={{
                border: `1px solid ${gold}30`,
                boxShadow: `0 20px 50px -20px ${gold}30`,
              }}
            >
              <GoldDivider color={gold} className="w-20 h-4 mx-auto mb-6 opacity-70" />
              <p
                className="text-lg sm:text-xl leading-relaxed italic"
                style={{ fontFamily: script, color: '#4b4b4b' }}
              >
                "{d.description}"
              </p>
              <GoldDivider color={gold} className="w-20 h-4 mx-auto mt-6 opacity-70 rotate-180" />
            </div>
          </div>
        </section>
      )}


{/* ============================================================
    GALERÍA
    ============================================================ */}
<Gallery
  media={media}
  category="Bautizos"
  accent={gold}
  text={dark}
  muted={muted}
  title="Momentos Sagrados"
  subtitle="Recuerdos"
  headingFont={elegant}
/>

      {/* ---------- DRESS CODE / NOTAS ---------- */}
      {(d.dressCode || d.notes) && (
        <section className="px-6 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex flex-col items-center gap-3">
              {d.dressCode && (
                <>
                  <p
                    className="text-[10px] tracking-[0.5em] uppercase"
                    style={{ color: gold, fontFamily: elegant, fontWeight: 600 }}
                  >
                    Código de vestimenta
                  </p>
                  <p
                    className="text-xl sm:text-2xl"
                    style={{ fontFamily: script, color: dark, fontStyle: 'italic' }}
                  >
                    {d.dressCode}
                  </p>
                </>
              )}
              {d.notes && (
                <p
                  className="text-sm max-w-md mt-2"
                  style={{ fontFamily: script, color: muted }}
                >
                  {d.notes}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---------- CIERRE ---------- */}
      <section className="px-6 py-16 text-center">
        <AngelWings color={gold} className="w-32 h-12 mx-auto mb-6 opacity-60" />
        <p
          className="text-lg sm:text-xl italic max-w-lg mx-auto"
          style={{ fontFamily: script, color: muted }}
        >
          Tu presencia es el mejor regalo.
          <br />
          ¡Te esperamos con mucho amor!
        </p>
        <GoldDivider color={gold} className="w-48 h-6 mx-auto mt-8 opacity-60" />
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="pb-12 text-center">
        <p
          className="text-xs"
          style={{ fontFamily: script, color: '#9a8863', letterSpacing: '0.1em' }}
        >
          Invitación creada con{' '}
          <b style={{ color: gold, fontFamily: elegant }}>Go Fiesta</b>
        </p>
      </footer>
    </div>
  );
}